import React, { useCallback, useEffect, useState } from "react";
import { ComboBox, IComboBoxOption, PrimaryButton, Text } from "@fluentui/react";

interface Props {
    environment: "LIVE" | "TEST";
}

const baseApi = process.env.REACT_APP_ZedXApiBaseUrl!;

export const CustomerAccessPanel: React.FC<Props> = ({ environment }) => {

    const [customerId, setCustomerId] = useState<string>("");
    const [customerOptions, setCustomerOptions] = useState<IComboBoxOption[]>([]);
    const [errorText, setErrorText] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const loadCustomerData = useCallback(() => {
        const storedCustomerIds: string[] =
            JSON.parse(localStorage.getItem(`customerIds_${environment}`) || "[]");

        const options = storedCustomerIds.map(id => ({
            key: id,
            text: id
        }));

        setCustomerOptions(options);

        const lastUsed = localStorage.getItem(`lastCustomerId_${environment}`);
        setCustomerId(lastUsed || "");
    }, [environment]);

    useEffect(() => {
        loadCustomerData();
    }, [loadCustomerData]);

    useEffect(() => {

        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                loadCustomerData();
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                loadCustomerData();
            }
        };

        const handleFocus = () => {
            loadCustomerData();
        };

        window.addEventListener("pageshow", handlePageShow);
        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("focus", handleFocus);

        return () => {
            window.removeEventListener("pageshow", handlePageShow);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("focus", handleFocus);
        };

    }, [loadCustomerData]);


    const restoreButtons = () => {
        setIsLoading(false);
    };


    const onContinue = async () => {

        setErrorText("");

        if (!customerId || customerId.length < 3) {
            setErrorText("Please enter a valid Customer ID.");
            return;
        }

        const authType = customerId.slice(-2).toLowerCase();
        const customerIdOnly = customerId.slice(0, -2);

        if (!(authType === "-a" || authType === "-z")) {
            setErrorText("Please enter a valid Customer ID.");
            return;
        }

        const liveOrTestCustomerId =
            environment === "LIVE" ? customerId : "_" + customerId;

        try {

            setIsLoading(true);

            const response = await fetch(
                `${baseApi}/customers/getcustomerinfo?c=${customerIdOnly}&environment=${environment}&authType=${authType}`
            );

            const data = await response.json();

            if (data.RedirectAddressMSAuth !== "" && data.RedirectAddressCustomAuth !== "") {

                // Load environment-specific stored IDs
                const storedCustomerIds: string[] =
                    JSON.parse(localStorage.getItem(`customerIds_${environment}`) || "[]");

                // Add to autocomplete list if new
                if (!storedCustomerIds.some(id => id.toLowerCase() === customerId.toLowerCase())) {

                    storedCustomerIds.push(customerId);

                    localStorage.setItem(
                        `customerIds_${environment}`,
                        JSON.stringify(storedCustomerIds)
                    );
                }

                localStorage.setItem("lastEnvironment", environment);
                
                // Save last used for environment
                localStorage.setItem(
                    `lastCustomerId_${environment}`,
                    customerId
                );

                const redirectAddress =
                    authType === "-a"
                        ? data.RedirectAddressMSAuth
                        : data.RedirectAddressCustomAuth;

                window.location.href =
                    `${redirectAddress}?c=${liveOrTestCustomerId}`;

            } else {

                const errMsg = data.ErrMsg || "Customer not found.";
                setErrorText(errMsg);
                restoreButtons();

            }

        } catch (error) {

            console.error("Error calling API:", error);
            restoreButtons();

        }
    };


    return (

        <div style={{ paddingLeft: '5px', maxWidth: "600px" }}>

            <h2 style={{ color: "rgb(112,146,190)", fontWeight: 400 }}>
                Welcome to ZedX Apps
            </h2>

            <p>
                To access the ZedX Apps platform, please enter your Customer ID.
                This should have been provided to you by your administrator.
            </p>

            <div style={{ marginTop: 30 }}>

                <Text variant="mediumPlus">
                    Customer ID ({environment === "LIVE" ? "Live Account" : "Training Account"})
                </Text>

                <ComboBox
                    allowFreeform
                    autoComplete="on"
                    options={customerOptions}
                    text={customerId}
                    onChange={(e, option, index, value) => {
                        setCustomerId(option?.text || value || "");
                    }}
                    style={{
                        width: "100%",
                        maxWidth: 400,
                        marginTop: 8
                    }}
                />

                {errorText && (
                    <div
                        style={{ color: "red", marginTop: 8 }}
                        dangerouslySetInnerHTML={{ __html: errorText }}
                    />
                )}

            </div>

            <div style={{ marginTop: 30 }}>

                <PrimaryButton
                    text="Continue"
                    onClick={onContinue}
                    disabled={isLoading}
                />

            </div>

        </div>
    );
};